import { Injectable } from '@angular/core';
import {
  ProductCharacteristicModel,
  ProductCompatibleEquipmentModel,
  ProductModel,
} from 'src/app/core/models/product.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BaseService } from 'src/app/core/base/base.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { ObjectApiInterface } from '../../../../core/base/interfaces/object-api.interface';
import { catchError, map, tap } from 'rxjs/operators';
import { TvaService } from '../../settings/tva/tva.service';
import { AuthService } from '../../../../core/services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class ProductService extends BaseService<ProductModel> {
  defaultSort = 'createdAt';

  // BehaviorSubject pour stocker les products
  private _products = new BehaviorSubject<ProductModel[]>([]);
  readonly products$ = this._products.asObservable();

  // BehaviorSubject pour les métadonnées (compteur, état du cache)
  private _productsMetadata = new BehaviorSubject<{
    totalData: number;
    loaded: boolean;
  }>({ totalData: 0, loaded: false });
  readonly productsMetadata$ = this._productsMetadata.asObservable();

  constructor(
    public http: HttpClient,
    private tvaService: TvaService,
    public authService: AuthService,
  ) {
    super(http, 'product', authService);
  }

  findAll(
    page?: number,
    perPage?: number,
    sortField: string = this.defaultSort,
    sortOrder: string = 'desc',
    search?: string,
    filters?: any,
  ): Observable<ObjectApiInterface<any[]>> {
    // // TODO: add priceTTC in product model with aggregation

    if (sortField === 'priceTTC') {
      sortField = 'priceHT';
    }
    return super.findAll(page, perPage, sortField, sortOrder, search, filters);
  }

  findAllService(
    page?: number,
    perPage?: number,
    sortField: string = this.defaultSort,
    sortOrder: string = 'desc',
    search?: string,
    filters?: any,
  ): Observable<ObjectApiInterface<any[]>> {
    // // TODO: add priceTTC in product model with aggregation

    if (sortField === 'priceTTC') {
      sortField = 'priceHT';
    }
    return this.http.get<ObjectApiInterface<any[]>>(`${this.baseUrlApi}/list-service`, {
      params: new HttpParams()
        .set('page', page?.toString() ?? '1')
        .set('perPage', perPage?.toString() ?? '500')
        .set('sort', `${sortField}@${sortOrder}`)
        .set('search', search ?? '')
        .set('filters', JSON.stringify(filters) ?? ''),
    });
  }

  // getOrderProductsPrestations(page?: number, perPage?: number, sortField: string = this.defaultSort, sortOrder: string = 'desc', search?: string, filters?: ProductModel | any): Observable<ObjectApiInterface<any[]>> {
  //     if (sortField === 'priceTTC') {
  //         sortField = 'priceHT';
  //     }
  //     return super.getOrderProductsPrestations(page, perPage, sortField, sortOrder, search, filters);
  // }

  findFiltered(
    page?: number,
    perPage?: number,
    sortField: string = this.defaultSort,
    sortOrder: string = 'desc',
    search?: string,
    filters?: any,
  ): Observable<ObjectApiInterface<any[]>> {
    return this.http.get<ObjectApiInterface<ProductModel[]>>(`${this.baseUrlApi}/list`, {
      params: new HttpParams()
        .set('page', page?.toString() ?? '1')
        .set('perPage', perPage?.toString() ?? '30')
        .set('sort', `${sortField}@${sortOrder}`)
        .set('search', search ?? '')
        .set('filters', JSON.stringify(filters) ?? ''),
    });
  }

  findById(id: string): Observable<ObjectApiInterface<ProductModel>> {
    return super.findById(id).pipe(
      map(data => {
        // if (data.data?.tva?.taux) data.data.priceTTC = data.data.priceHT + data.data.priceHT * data.data?.tva?.taux / 100;
        return data;
      }),
    );
  }

  addInternalProducts(productIds: string[], contractId: string): Observable<ObjectApiInterface<any>> {
    return this.http.post<ObjectApiInterface<any>>(`${this.baseUrlApi}/addInternal`, { productIds, contractId });
  }

  checkDependencies(product: ProductModel) {
    return this.http.post<ObjectApiInterface<any>>(`${this.baseUrlApi}/checkDependencies/${product._id}`, {});
  }

  archive(entity: ProductModel): Observable<ObjectApiInterface<ProductModel>> {
    return this.http.get<ObjectApiInterface<ProductModel>>(`${this.baseUrlApi}/internal-archive/${entity._id}`);
  }

  unarchive(entity: ProductModel): Observable<ObjectApiInterface<ProductModel>> {
    return this.http.get<ObjectApiInterface<ProductModel>>(`${this.baseUrlApi}/internal-unarchive/${entity._id}`);
  }

  changeAutoDependenciesAndArchive(_id: string): Observable<ObjectApiInterface<ProductModel>> {
    return this.archive({ _id } as ProductModel);
  }

  getsupplierRelation(_id): Observable<ObjectApiInterface<ProductModel[]>> {
    return this.http.get<ObjectApiInterface<ProductModel[]>>(`${this.baseUrlApi}/getsupplierRelation/${_id}`);
  }

  getGenericProductRelation(_id): Observable<ObjectApiInterface<ProductModel[]>> {
    return this.http.get<ObjectApiInterface<ProductModel[]>>(`${this.baseUrlApi}/getGenericProductRelation/${_id}`);
  }

  update(entity: ProductModel): Observable<ObjectApiInterface<ProductModel>> {
    return super.update(entity).pipe(
      map(data => {
        const tvaRate = this.tvaService.getRateByDate(data.data?.tva);
        if (tvaRate?.rate) {
          data.data.priceTTC = data.data.priceHT + (data.data.priceHT * tvaRate.rate) / 100;
        }
        this._entity.next(data.data);
        return data;
      }),
    );
  }

  addCharacteristic(productId: string, data: any): Observable<ObjectApiInterface<ProductModel>> {
    return this.http
      .post<ObjectApiInterface<ProductModel>>(`${this.baseUrlApi}/${productId}/addCharacteristic`, data)
      .pipe(
        map(x => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  updateCharacteristic(
    productId: string,
    characteristicId: string,
    data: ProductCharacteristicModel,
  ): Observable<ObjectApiInterface<ProductModel>> {
    return this.http
      .post<
        ObjectApiInterface<ProductModel>
      >(`${this.baseUrlApi}/${productId}/updateCharacteristic/${characteristicId}`, data)
      .pipe(
        map(x => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  deleteCharacteristic(productId: string, dataId: any): Observable<ObjectApiInterface<ProductModel>> {
    return this.http
      .delete<ObjectApiInterface<ProductModel>>(`${this.baseUrlApi}/${productId}/deleteCharacteristic/${dataId}`)
      .pipe(
        map(x => {
          this._entity.value.characteristics = this._entity.value.characteristics.filter(y => y._id !== dataId);
          return x;
        }),
      );
  }

  addComplatibleEquipment(productId: string, data: any) {
    return this.http
      .post<ObjectApiInterface<ProductModel>>(`${this.baseUrlApi}/${productId}/addCompatibleEquipment`, data)
      .pipe(
        map(x => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  deleteCompatibleEquipment(productId: string, compatibleEquipmentid: any) {
    return this.http
      .delete<
        ObjectApiInterface<ProductCompatibleEquipmentModel>
      >(`${this.baseUrlApi}/${productId}/deleteCompatibleEquipment/${compatibleEquipmentid}`)
      .pipe(
        map(x => {
          const entity = this._entity.value;
          entity.compatibleEquipment = entity.compatibleEquipment.filter(y => y._id !== compatibleEquipmentid);
          this._entity.next(entity);
          return x;
        }),
      );
  }

  addGenerics(data: any) {
    return this.http.post<ObjectApiInterface<any>>(`${this.baseUrlApi}/addGenerics`, data).pipe(
      map(x => {
        // this._entity.next(x.data);
        return x;
      }),
    );
  }

  // import(entity: ProductModel): Observable<ObjectApiInterface<ProductModel>> {
  //      return this.http.post<ObjectApiInterface<ProductModel>>(`${this.baseUrlApi}/importProductContract`, entity);
  // }

  importProductContractItems(entity: ProductModel, contractId: string, quoteId?: string): Observable<any> {
    console.log(quoteId);
    const url = quoteId
      ? `${this.baseUrlApi}/importProductContract/${contractId}?quoteId=${quoteId}`
      : `${this.baseUrlApi}/importProductContract/${contractId}`;

    // ✅ Configuration avec timeout étendu pour gros imports
    const httpOptions = {
      timeout: 5 * 60 * 1000, // 5 minutes timeout
      reportProgress: true,
      observe: 'body' as const,
    };

    return this.http.post<ObjectApiInterface<ProductModel>>(url, entity, httpOptions);
  }

  importProductContractModificationItems(entity: ProductModel, contractId: string): Observable<any> {
    return this.http.post<ObjectApiInterface<ProductModel>>(
      `${this.baseUrlApi}/importProductContractModificationItems/${contractId}`,
      entity,
    );
  }

  downloadExcelTemplateProductContract(contractId: string): Observable<ObjectApiInterface<ProductModel>> {
    return this.http.get<ObjectApiInterface<ProductModel>>(
      `${this.baseUrlApi}/downloadProductContractTemplate/${contractId}`,
    );
  }

  downloadExcelTemplateProductContractModificationItem(
    contractId: string,
  ): Observable<ObjectApiInterface<ProductModel>> {
    return this.http.get<ObjectApiInterface<ProductModel>>(
      `${this.baseUrlApi}/downloadExcelTemplateProductContractModificationItem/${contractId}`,
    );
  }

  exportBulkUpdate(contractId: string): Observable<ObjectApiInterface<any>> {
    return this.http.get<ObjectApiInterface<any>>(`${this.baseUrlApi}/export-bulk-update`, {
      params: new HttpParams().set('contractId', contractId),
    });
  }

  importBulkUpdate(data: any): Observable<ObjectApiInterface<any>> {
    return this.http.post<ObjectApiInterface<any>>(`${this.baseUrlApi}/import-bulk-update`, data);
  }

  suspend(entity: ProductModel): Observable<ObjectApiInterface<ProductModel>> {
    return this.http.get<ObjectApiInterface<ProductModel>>(`${this.baseUrlApi}/suspend/${entity._id}`);
  }

  unsuspend(entity: ProductModel): Observable<ObjectApiInterface<ProductModel>> {
    return this.http.get<ObjectApiInterface<ProductModel>>(`${this.baseUrlApi}/unsuspend/${entity._id}`);
  }

  // getGenProductByGenEngagement(genEngagementId: string): Observable<ObjectApiInterface<GenericProductModel[]>> {
  //     console.log('genEngagementId', genEngagementId);
  //     return this.http.get<ObjectApiInterface<GenericProductModel[]>>(`${this.baseUrlApi}/getGenProductByGenEngagement/${genEngagementId}`);
  // }

  getProductsInTechnicalPerimeters(contractIds: any, itemIds: any) {
    return this.http
      .post<ObjectApiInterface<any>>(`${this.baseUrlApi}/getProductsInTechnicalPerimeters`, { contractIds, itemIds })
      .pipe(
        map(x => {
          // this._entity.next(x.data);
          return x;
        }),
      );
  }

  validateGenericAdministration(data: any[]) {
    return this.http.patch<ObjectApiInterface<any>>(`${this.baseUrlApi}/validateGenericAdministration`, data).pipe(
      map(x => {
        // this._entity.next(x.data);
        return x;
      }),
    );
  }

  /**
   * Charge les produits ET les stocke dans le BehaviorSubject (cache)
   */
  loadProducts(
    page?: number,
    perPage?: number,
    sortField?: string,
    sortOrder?: string,
    search?: string,
    filters?: any,
  ): Observable<ObjectApiInterface<ProductModel[]>> {
    return this.findAll(page, perPage, sortField, sortOrder, search, filters).pipe(
      tap(res => {
        // Stocker dans le BehaviorSubject
        this._products.next(res.data);
        this._productsMetadata.next({
          totalData: res.totalData,
          loaded: true,
        });
      }),
    );
  }

  /**
   * Vide le cache des produits
   */
  clearCache(): void {
    this._products.next([]);
    this._productsMetadata.next({ totalData: 0, loaded: false });
  }
}
